import { TabularTypeDataset } from 'app/model/dataset/tabular-type.dataset';
import {
    AzureBlobStorageLinkedService,
    AzureDataExplorerDataset,
    AzureSqlDWTableDataset,
    azureStorageAuthTypes,
    DataFlowParameterConfig,
    DelimitedTextDataset,
    IAttribute,
    IModel,
    IValidationContext,
    IViolation,
    LakehouseTableDataset,
    LinkedService,
    ModelConstants,
    NamedArray,
    OwnedSet,
    Parameter,
    ParquetDataset
} from 'shell-models-lib';
import { Metamodel } from 'app/model/base/model';
import { Dataset } from 'app/model/dataset/dataset';

import { DataFlow } from 'app/model/dataflow/dataflow';
import { DataFlowColumn } from 'app/model/dataset/dataflowcolumn';
import { format, sanitizeAndFormatString, stringIsNullOrEmpty, stringIsNullOrWhitespace } from 'app/shared/utils/string.util';
import { IColumnMappingTransformation } from 'app/model/dataflow/column-mapping-transformation-utilities';
import { DataFlowStream, Transformation } from './transformation';
import { FileTypeDataset } from 'app/model/dataset/file-type.dataset';
import { BaseColumnMappingTransformation } from './base-column-mapping-transformation';
import { IDataFlowDefaultTypeSettings } from './IDataflow-default-format';
import { mdfConnectionTypeValidator, mdfFormatValidator, transformationDatasetValidator, sourceSinkLinkedServiceValidator } from './transformation-dataset-validator';
import { Constants } from 'app/shared/shared-constants';
import { DataFlowMapColumn } from './dataflow-map.column';
import { DataFlowExpression } from './dataflowexpression';
import { ComplexDataFlowType } from './dataflow-complex-datatype';
import { IntegerDataFlowType, BooleanDataFlowType, StringDataFlowType, ArrayDataFlowType } from './dataflow-datatype';
import { isTruthy } from './dataflow-expression-utilities';
import { AlterRowTransformation } from './alterrowtransformation';
import { AssertTransformation } from './asserttransformation';
import { isFileTypeDataset, isFileTypeCategoryDataset } from 'app/shared/utils/dataset-validation.util';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { AzureCosmosDbV2Dataset } from '../dataset/azure-cosmos-db.v2.dataset';
import { DataFlowDatasetSettings, DatasetFormatType } from './dataset-settings/dataflow-dataset-settings';
import { getDataFlowBaseArguments } from 'app/shared/components/parameterizable/parameterizable-dataflow-utilities';
import { updateIdentifiers, asyncUpdateIdentifiers } from './dataflow-expression-update-identifiers';
import { GlobalVariables } from 'app/services/globals';
import { AdobeIntegrationLinkedService } from '../linkedservice/adobe-integration.linkedservice';
import { DeltaSettings } from './dataset-settings/delta-dataset-settings';
import { TableActionOption, TabularTypeSettings } from './dataset-settings/tabular-type-settings';
import { SqlBaseSettings } from './dataset-settings/sql-base-settings';
import { workspaceDatabaseValidator, workspaceTableValidator } from './workspace-database-validators';
import { RestSettings } from './dataset-settings/rest-settings';
import { RestResourceDataset } from '../dataset/rest-resource.dataset';
import { DatasetRegistryService } from 'app/registries/dataset/dataset-registry.service';
import { AdxSettings } from 'app/model/dataflow/dataset-settings/adx-settings';
import { DynamicsSettings } from 'app/model/dataflow/dataset-settings/dynamics-settings';
import { DynamicsEntityDataset } from 'app/model/dataset/dynamics-entity.dataset';
import { CommonDataServiceEntityDataset } from '../dataset/common-data-service.dataset';
import { DynamicsCrmEntityDataset } from '../dataset/dynamics-crm.dataset';
import {
    findDataFlowSource,
    isDataflowSourceEnableCdc,
    isDataflowSinkEnableAlterRow,
    updateDatasetSettingsCache,
    isCosmosDBAnalyticalType,
    initializeDataflowSink,
    findDataFlowSink,
    isCassandraType
} from 'app/model/dataflow/dataflow-utils';
import { CosmosSqlSettings } from './dataset-settings/cosmos-sql-settings';
import { CassandraDataset } from '../dataset/cassandra.dataset';
import { IResourceContext } from 'app/multi-contexts/resource-context.typings';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { EventHubsSettings } from './dataset-settings/event-hubs-settings';
import { DataflowFormatRegistryService } from 'app/registries/dataflow/dataflow-format-registry.service';

export const savePolicies = {
    overwrite: 'overwrite',
    append: 'append',
    error: 'error',
    skip: 'skip'
};

export enum FileNameOption {
    Default,
    FilePattern,
    RowUrlColumn,
    RowFolderUrlColumn,
    PartitionFileNames,
    SingleFile
}

export interface IKeyColumnsEntity {
    keyColumns: DataFlowExpression[];
    isListOfKeyColumns: boolean;
}

@Metamodel.model({
    name: ModelConstants.dataFlowSink,
    display: [stringResources.DataFlowSinkLabel, stringResources.DataFlowSinksLabel],
    referable: true,
    constraints: {
        name: {
            required: true,
            pattern: new RegExp('^[^-\\s][^\\\\/:*?"<>|]{1,140}$'),
            uniqueModelName: true
        },
        inputColumns: {
            validator: DataFlowSink.validateColumnNames
        },
        keyColumnsMappings: {
            validator: DataFlowSink.validateKeyColumnMappings
        }
    },
    validator: DataFlowSink.validateDataFlowSink
})
export class DataFlowSink extends BaseColumnMappingTransformation implements IColumnMappingTransformation, IKeyColumnsEntity {
    public static readonly MODEL: IModel;

    public static readonly STAGING_LINKED_SERVICE_PARAMETERS: IAttribute;
    public static readonly SQL_ERROR_STORAGE_PATH: IAttribute;
    public static readonly ASSERT_FAILURE_STORAGE_PATH: IAttribute;
    public static readonly MDF_CONNECTION_TYPE: IAttribute;
    public static readonly DATASET_SETTINGS: IAttribute;

    @Metamodel.attribute({
        display: 'Dataset',
        constraint: {
            validator: DataFlowSink.validateSinkDataset
        },
        reference: true,
        type: Dataset.MODEL
    })
    public sinkDataset: Dataset;

    @Metamodel.attribute({
        display: stringResources.SinkLinkedService,
        constraint: {
            validator: sourceSinkLinkedServiceValidator
        },
        reference: true,
        type: LinkedService.MODEL
    })
    public sinkLinkedService: LinkedService;

    /**
     * Optional Fabric DMTS credential connection paired with `sinkLinkedService` when the sink is a Fabric
     * artifact (e.g., a Lakehouse). Mirrors `Dataset.dmtsCredentialForArtifact` but at the sink-transformation
     * level for inline (no-dataset) MDF sinks where there is no `Dataset` to host the credential reference.
     * Not persisted by the dataflow DSL adapter today; used as a transient binding so the trident-connection-select
     * component can correctly group/render the credential connection alongside the artifact picker.
     */
    @Metamodel.attribute({
        reference: true,
        type: LinkedService.MODEL
    })
    public sinkDmtsCredentialLinkedService?: LinkedService;

    /**
     * MDF: Tracks the selected connection type (LakehouseFiles, LakehouseTables, Warehouse) for validation.
     * Used by mdfConnectionTypeValidator to show appropriate error messages.
     */
    @Metamodel.attribute({
        constraint: {
            validator: mdfConnectionTypeValidator
        }
    })
    public mdfConnectionType?: DatasetFormatType;

    @Metamodel.attribute({
        display: stringResources.LinkedService,
        reference: true,
        type: LinkedService.MODEL
    })
    public schemaLinkedService: LinkedService;

    /**
     * Dataset-specific settings (format, schema, table, etc.) for inline datasets.
     * Uses validateDatasetSettings which includes:
     * - MDF format validation (via mdfFormatValidator, same as source)
     * - Sink-specific validations (update methods, key columns, CDC)
     */
    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSink.validateDatasetSettings
        }
    })
    public datasetSettings: DataFlowDatasetSettings;

    public inlineDataSetColumns: NamedArray<DataFlowColumn> = NamedArray.create<DataFlowColumn>(this);

    public maxConcurrentConnections?: number;
    public savePolicy: string = savePolicies.overwrite;

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSink.validatePartitionFileNames
        }
    })
    public partitionFileNames: DataFlowExpression[] = [this.createTemplatableExpression()];

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSink.validatePreSQLScripts
        }
    })
    public preSQLScript: DataFlowExpression | DataFlowExpression[] = [this.createTemplatableExpression()];

    public preFileCommand: DataFlowExpression[] = [this.createTemplatableExpression()];

    public isListofPreFileCommands = true;

    public postFileCommand: DataFlowExpression[] = [this.createTemplatableExpression()];

    public isListOfPostFileCommands = true;

    public isListOfPreSQLScripts = true;

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSink.validatePostSQLScripts
        }
    })
    public postSQLScript: DataFlowExpression | DataFlowExpression[] = [this.createTemplatableExpression()];

    public isListOfPostSQLScripts = true;

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSink.validateKeyColumns
        }
    })
    public keyColumns: DataFlowExpression[] = [this.createTemplatableExpression()];

    @Metamodel.attribute({
        constraint: {
            validator: (owner: DataFlowSink): IViolation[] | undefined => {
                if (owner.sinkDataset instanceof AzureCosmosDbV2Dataset && owner.tableActionOption === TableActionOption.Truncate) {
                    return [{
                        context: owner,
                        message: () => stringResources.DataFlowSinkTruncateNotSupport
                    }];
                }
                return undefined;
            }
        }
    })
    public tableActionOption: TableActionOption = TableActionOption.None;

    @Metamodel.attribute({
        display: stringResources.DataFlowErrorSpecifyRowUrl_Sink,
        constraint: {
            validator: DataFlowSink.validateRowUrlColumn
        }
    })
    public rowUrl: DataFlowExpression = this.createTemplatableExpression();

    @Metamodel.attribute({
        display: stringResources.DataFlowErrorSpecifyRowUrl_Sink,
        constraint: {
            validator: DataFlowSink.rowFolderUrlColumn
        }
    })
    public rowFolderUrlColumn: DataFlowExpression = this.createTemplatableExpression();

    @Metamodel.attribute({
    })
    public fileNameOption: FileNameOption = FileNameOption.Default;
    public dataTypeSettings: IDataFlowDefaultTypeSettings = {};
    public staged: boolean = true;
    public useCopyCommandForStaging: boolean = true;
    public isInlineDataset: boolean = false;
    public isCacheSink: boolean = false;
    public isListOfKeyColumns: boolean = true;
    public snowflakeStageInsert: boolean = true;
    public cosmosUseTotalWriteThroughputBudget: boolean = true;
    public isWorkspaceDatabase: boolean = false;
    public workspaceDatabaseType = this.createTemplatableExpression(StringDataFlowType.TYPE, 'spark');
    @Metamodel.attribute({
        constraint: {
            validator: workspaceDatabaseValidator
        }
    })
    public workspaceDatabase: DataFlowExpression = this.createTemplatableExpression();
    @Metamodel.attribute({
        constraint: {
            validator: workspaceTableValidator
        }
    })
    public workspaceTable: DataFlowExpression = this.createTemplatableExpression();

    public umask: DataFlowExpression = this.createTemplatableExpression(StringDataFlowType.TYPE, '022');

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSink.validateFilePattern
        }
    })
    public filePattern: DataFlowExpression = this.createTemplatableExpression();

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSink.validateSingleFile
        }
    })
    public singleFile: DataFlowExpression = this.createTemplatableExpression();

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSink.validateEmptyLinesAsHeader
        }
    })
    public emptyLinesAsHeader: DataFlowExpression = this.createTemplatableExpression(IntegerDataFlowType.TYPE);

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSink.validateBatchSize
        }
    })
    public batchSize: DataFlowExpression = this.createTemplatableExpression(IntegerDataFlowType.TYPE, '');

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSink.validateTTL
        }
    })
    public ttl: DataFlowExpression = this.createTemplatableExpression(IntegerDataFlowType.TYPE, '');

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSink.validateSaveOrder
        }
    })
    public saveOrder: DataFlowExpression = this.createTemplatableExpression(IntegerDataFlowType.TYPE, '');

    public consistencyLevel: string = 'one';

    @Metamodel.attribute({
        display: stringResources.DataFlowSourcePanelAllowSchemaDrift,
        constraint: {
            validator: (sink: DataFlowSink, value: DataFlowExpression) => {
                const violations: IViolation[] = [];
                if (sink && !isTruthy(sink.allowSchemaDrift) && sink.sinkDataset && sink.sinkDataset.columns.length === 0) {
                    violations.push({
                        context: sink,
                        message: () => sanitizeAndFormatString(stringResources.DataFlowSinkEmptySchema, sink.sinkDataset.name, sink.name)
                    });
                }
                violations.push(...Transformation.validateTemplateExpressions(sink, [value], stringResources.DataFlowSourcePanelAllowSchemaDrift));
                return violations;
            }
        }
    })
    public allowSchemaDrift: DataFlowExpression = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'true');

    public hasSchemaDriftAttribute: boolean = false;

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSink.validateSchema
        }
    })
    public validateSchema: DataFlowExpression = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSink.validateAllowInsert
        }
    })
    public allowInsert: DataFlowExpression = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'true');

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSink.validateAllowDelete
        }
    })
    public allowDelete: DataFlowExpression = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSink.validateAllowUpsert
        }
    })
    public allowUpsert: DataFlowExpression = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSink.validateAllowUpdate
        }
    })
    public allowUpdate: DataFlowExpression = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSink.validateskipKeyWrites
        }
    })
    public skipKeyWrites: DataFlowExpression = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSink.validateClearFolder
        }
    })
    public clearFolder: DataFlowExpression = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSink.validateQuoteAll
        }
    })
    public quoteAll: DataFlowExpression = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSink.validateCacheSinkOutput
        }
    })
    public cacheSinkOutput: DataFlowExpression = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSink.validateHeaders
        }
    })
    public delimitedTextHeader: DataFlowExpression = this.createTemplatableExpression(ArrayDataFlowType.TYPE);

    // cosmos settings
    @Metamodel.attribute({
        constraint: {
            validator: (entity: DataFlowSink, value: DataFlowExpression): IViolation[] | undefined => {
                if (entity.sinkDataset && Metamodel.modelOf(entity.sinkDataset).name === adfModelTypeMap.cosmosSql.datasetType) {
                    if (entity.tableActionOption === TableActionOption.Recreate && stringIsNullOrEmpty(value.expression)) {
                        return [{
                            message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessageNoVowel, stringResources.ThroughputLabel, stringResources.DataFlowSinkLabel, entity.name)
                        }];
                    }
                    if (!value.templateExpression && value.expression) {
                        const val = parseInt(value.expression, 10);
                        if (isNaN(val) || val < 400 || val > 1000000 || val % 100 !== 0) {
                            return [{
                                message: () => stringResources.ThroughputErrorMessage
                            }];
                        }
                    }
                    return Transformation.validateTemplateExpressions(entity, [value], stringResources.ThroughputLabel);
                }
                return undefined;
            }
        }
    })
    public throughput: DataFlowExpression = this.createTemplatableExpression(IntegerDataFlowType.TYPE);

    @Metamodel.attribute({
        constraint: {
            validator: (entity: DataFlowSink, value: DataFlowExpression): IViolation[] | undefined => {
                if (entity.sinkDataset && Metamodel.modelOf(entity.sinkDataset).name === adfModelTypeMap.cosmosSql.datasetType) {
                    if (entity.partitionKeyRequired && stringIsNullOrEmpty(value.expression)) {
                        return [{
                            message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessageNoVowel, stringResources.PartitionKeyLabel, stringResources.DataFlowSinkLabel, entity.name)
                        }];
                    }
                    return Transformation.validateTemplateExpressions(entity, [value], stringResources.PartitionKeyLabel);
                }
                return undefined;
            }
        }
    })
    public partitionKey: DataFlowExpression = this.createTemplatableExpression();

    @Metamodel.attribute({
        constraint: {
            validator: (entity: DataFlowSink, value: DataFlowExpression): IViolation[] | undefined => {
                if (entity.sinkDataset && Metamodel.modelOf(entity.sinkDataset).name === adfModelTypeMap.cosmosSql.datasetType) {
                    if (!value.templateExpression && value.expression) {
                        const val = parseInt(value.expression, 10);
                        if (isNaN(val) || val <= 0) {
                            return [{
                                message: () => stringResources.WriteThroughPutBudgetErrorMessage
                            }];
                        }
                    }
                }
                return undefined;
            }
        }
    })
    public writeThroughPutBudget: DataFlowExpression = this.createTemplatableExpression(IntegerDataFlowType.TYPE);

    public useTmpDB: boolean = true;
    public stagingSchemaName: DataFlowExpression = this.createTemplatableExpression();

    public hasAlterRowUpstream: boolean = false;
    public hasAssertUpstream: boolean = false;

    public insertHttpMethod: DataFlowExpression = this.createTemplatableExpression(StringDataFlowType.TYPE, 'POST');
    public deleteHttpMethod: DataFlowExpression = this.createTemplatableExpression(StringDataFlowType.TYPE, 'DELETE');
    public upsertHttpMethod: DataFlowExpression = this.createTemplatableExpression(StringDataFlowType.TYPE, 'PUT');
    public updateHttpMethod: DataFlowExpression = this.createTemplatableExpression(StringDataFlowType.TYPE, 'PATCH');
    public requestTimeout: DataFlowExpression = this.createTemplatableExpression(IntegerDataFlowType.TYPE, '30');
    public requestInterval: DataFlowExpression = this.createTemplatableExpression(IntegerDataFlowType.TYPE, '0');
    public additionalHeaders: DataFlowParameterConfig[] = [];
    public queryParameters: DataFlowParameterConfig[] = [];
    public httpCompressionType: DataFlowExpression = this.createTemplatableExpression();
    public restDataFormat = 'json';
    public dateFormat: string;
    public timestampFormat: string;

    @Metamodel.attribute({
        constraint: {
            validator: (owner: DataFlowSink, value: DataFlowExpression): IViolation[] => {
                const violations = [];
                if ((isTruthy(owner.allowDelete) || isTruthy(owner.allowInsert) || isTruthy(owner.allowUpdate) || isTruthy(owner.allowUpsert)) && owner.sinkDataset instanceof RestResourceDataset) {
                    if (isTruthy(owner.skipRowRelativeUrl) && (!value || !value.expression)) {
                        violations.push({
                            context: owner,
                            message: () => stringResources.DataFlowSkipRowRelativeUrlError
                        });
                    }
                    if (owner.restDataFormat && owner.batchSize && !owner.batchSize.templateExpression && owner.batchSize.expression && parseInt(owner.batchSize.expression, 10) > 1
                        && value && value.expression) {
                        violations.push({
                            context: owner,
                            message: () => stringResources.DataFlowBatchSizeRowRelativeUrlError
                        });
                    }
                }
                return violations;
            }
        }
    })
    public rowRelativeUrl = this.createTemplatableExpression();
    public skipRowRelativeUrl = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');

    public enableErrorHandling: DataFlowExpression = this.createTemplatableExpression(StringDataFlowType.TYPE, 'stopOnFirstError');

    @Metamodel.attribute({
        reference: true,
        constraint: {
            validator: DataFlowSink._validateFaultToleranceLinkedService
        }
    })
    public faultToleranceLinkedService?: LinkedService;

    // for lakehousetable dataset
    public mergeSchema = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');
    public autoCompact = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');
    public optimizedWrite = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');

    @Metamodel.attribute({
        display: stringResources.Dataflow_StagingPath,
        association: 'SQL_ERROR_STORAGE_PATH',
        templatable: true
    })
    @Metamodel.attribute({
        display: stringResources.Dataflow_StagingPath,
        association: 'ASSERT_FAILURE_STORAGE_PATH',
        templatable: true
    })

    public sqlErrorStoragePath?: string;
    public sqlErrorContainer?: DataFlowExpression = this.createTemplatableExpression(StringDataFlowType.TYPE);
    public sqlErrorFileSystem?: DataFlowExpression = this.createTemplatableExpression(StringDataFlowType.TYPE);
    public sqlErrorFolderPath?: DataFlowExpression = this.createTemplatableExpression(StringDataFlowType.TYPE);

    public assertFailureStoragePath?: string;
    public assertFailureContainer?: DataFlowExpression = this.createTemplatableExpression(StringDataFlowType.TYPE);
    public assertFailureFileSystem?: DataFlowExpression = this.createTemplatableExpression(StringDataFlowType.TYPE);
    public assertFailureFolderPath?: DataFlowExpression = this.createTemplatableExpression(StringDataFlowType.TYPE);

    public outputRejectedData?: DataFlowExpression = this.createTemplatableExpression(BooleanDataFlowType.TYPE);
    public dataVisibility?: DataFlowExpression = this.createTemplatableExpression(StringDataFlowType.TYPE, 'single');
    public reportSuccessOnError?: DataFlowExpression = this.createTemplatableExpression(BooleanDataFlowType.TYPE);
    public outputAssertFailedRowsToAltStorage?: DataFlowExpression = this.createTemplatableExpression(BooleanDataFlowType.TYPE);
    public outputAssertFailedRowsToSink?: DataFlowExpression = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'true');
    // Only used to set in dsl when outputAssertFailedRowsToSink is false
    public ignoreAssertFailedRows?: DataFlowExpression = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');

    // for ADX
    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSink.validateTimeout
        }
    })
    public timeout: DataFlowExpression = this.createTemplatableExpression(IntegerDataFlowType.TYPE);

    // for Dynamics
    @Metamodel.attribute({})
    public alternateKeyName: DataFlowExpression = this.createTemplatableExpression();

    constructor(name?: string, description?: string) {
        super(name, description);
        this.skipDuplicateMapInputs.expression = 'true';
        this.skipDuplicateMapOutputs.expression = 'true';
    }

    public static validateColumnNames(dataFlowSink: DataFlowSink): IViolation[] | undefined {
        const violations: IViolation[] = [];

        if (dataFlowSink.schema.find((column) => column instanceof DataFlowMapColumn && column.columnRef
            && !dataFlowSink.distinctInputColumnRefs.some((inputColumn) => inputColumn.column.name === column.columnRef.column.name))) {
            violations.push({
                context: dataFlowSink,
                message: () => sanitizeAndFormatString(stringResources.DataFlowSinkColumnReferenceMismatch, dataFlowSink.name)
            });
        }

        return violations;
    }

    public static async validateSinkDataset(dataFlowSink: DataFlowSink, val: Dataset, violation?: IViolation, context?: IValidationContext): Promise<IViolation[] | undefined> {
        const violations: IViolation[] = [];
        const dataSetValidation = await transformationDatasetValidator(dataFlowSink, dataFlowSink.sinkDataset, violation, context);
        if (dataSetValidation) {
            violations.push(...dataSetValidation);
        }
        if (dataFlowSink.sinkDataset && dataFlowSink.sinkDataset instanceof FileTypeDataset && dataFlowSink.sinkDataset.fileName) {
            violations.push({
                context: dataFlowSink,
                message: () => sanitizeAndFormatString(stringResources.DataFlowSinkDatasetContainsFilePath, dataFlowSink.sinkDataset?.name || '')
            });
        }

        if (dataFlowSink.sinkDataset && dataFlowSink.sinkDataset instanceof TabularTypeDataset) {
            const tableNameAttribute = Metamodel.modelOf(dataFlowSink.sinkDataset).attribute('tableName');
            const templatizedTableName = tableNameAttribute.templateValue(dataFlowSink.sinkDataset);
            const tableAttribute = Metamodel.modelOf(dataFlowSink.sinkDataset).attribute('table');
            const templatizedTable = tableAttribute.templateValue(dataFlowSink.sinkDataset);
            if (!templatizedTableName && !dataFlowSink.sinkDataset.tableName
                && !templatizedTable && !dataFlowSink.sinkDataset.table) {
                violations.push({
                    context: dataFlowSink,
                    redirectTo: {
                        entity: dataFlowSink.sinkDataset,
                        attribute: TabularTypeDataset.TABLE_NAME
                    },
                    message: () => sanitizeAndFormatString(stringResources.Empty_TableName_Error_Message, dataFlowSink.sinkDataset?.name || '')
                });
            }
            // kusto, cosmos, dynamics, & dataverse don't have key columns
            if (
                !(dataFlowSink.sinkDataset instanceof AzureCosmosDbV2Dataset)
                && !(dataFlowSink.sinkDataset instanceof DynamicsEntityDataset)
                && !(dataFlowSink.sinkDataset instanceof DynamicsCrmEntityDataset)
                && !(dataFlowSink.sinkDataset instanceof CommonDataServiceEntityDataset)
                && !(dataFlowSink.sinkDataset instanceof AzureDataExplorerDataset)
            ) {
                if (dataFlowSink.allowInsert.expression === 'false'
                    && dataFlowSink.allowDelete.expression === 'false'
                    && dataFlowSink.allowUpdate.expression === 'false'
                    && dataFlowSink.allowUpsert.expression === 'false') {
                    violations.push({
                        context: dataFlowSink,
                        message: () => stringResources.DataFlowSinkNoUpdateMethodSelected
                    });
                }
                if ((dataFlowSink.allowDelete.expression === 'true' || dataFlowSink.allowUpdate.expression === 'true' || dataFlowSink.allowUpsert.expression === 'true')
                    && (dataFlowSink.keyColumns.every((column) => !column || !column.expression)
                        || (!dataFlowSink.isListOfKeyColumns && (!dataFlowSink.keyColumns[0] || !dataFlowSink.keyColumns[0].expression)))) {
                    violations.push({
                        context: dataFlowSink,
                        message: () => stringResources.DataFlowSinkRequiresKeyColumn
                    });
                }
                if (dataFlowSink.skipKeyWrites.expression === 'true' && dataFlowSink.keyColumns.every((column) => !column || !column.expression)) {
                    violations.push({
                        context: dataFlowSink,
                        message: () => sanitizeAndFormatString(stringResources.DataFlowSinkRequiresKeyColumnSkipKeyWrites, dataFlowSink.sinkDataset?.name || '')
                    });
                }
            }

            if (dataFlowSink.enableErrorHandling.expression !== 'stopOnFirstError' && dataFlowSink.outputRejectedData.expression === 'true') {
                if (!dataFlowSink.faultToleranceLinkedService) {
                    violations.push({
                        context: dataFlowSink,
                        message: () => sanitizeAndFormatString(stringResources.DataFlowSinkRequiresErrorLogging, dataFlowSink.sinkDataset?.name || '')
                    });
                }
                if (!dataFlowSink.sqlErrorContainer.expression && !dataFlowSink.sqlErrorFileSystem.expression) {
                    violations.push({
                        context: dataFlowSink,
                        message: () => sanitizeAndFormatString(stringResources.DataFlowSinkRequiresContainerErrorLogging, dataFlowSink.sinkDataset?.name || '')
                    });
                }
            }
        }

        const complexColumns = dataFlowSink.outputColumns().filter((outputColumn) => outputColumn.dataFlowColumnType instanceof ComplexDataFlowType);
        const datasetRegistrySvc = await getServiceInstance<DatasetRegistryService>(context.contextData, 'DatasetRegistryService');
        if (!dataFlowSink.isCacheSink && !dataFlowSink.isWorkspaceDatabase && !!complexColumns.length && !(
            (dataFlowSink.isInlineDataset && dataFlowSink.sinkLinkedService instanceof AdobeIntegrationLinkedService)
            || (dataFlowSink.isInlineDataset && dataFlowSink.datasetSettings instanceof DeltaSettings)
            || (dataFlowSink.isInlineDataset && dataFlowSink.datasetSettings instanceof EventHubsSettings)
            || (dataFlowSink.isInlineDataset && dataFlowSink.datasetSettings?.getDatasetType() &&
                datasetRegistrySvc.getDatasetConfiguration(dataFlowSink.datasetSettings.getDatasetType()).supportDataFlowWriteComplexStructure)
            || (!dataFlowSink.isInlineDataset && dataFlowSink.sinkDataset &&
                datasetRegistrySvc.getDatasetConfiguration(Metamodel.modelOf(dataFlowSink.sinkDataset).name).supportDataFlowWriteComplexStructure)
        )) {
            violations.push({
                context: dataFlowSink,
                message: () => sanitizeAndFormatString(__IS_A365__ ? stringResources.DataFlowSinkSchemaOutputColumnComplexA365 : stringResources.DataFlowSinkSchemaOutputColumnComplex,
                    complexColumns.map((column) => column.name).join('\', \''))
            });
        }

        return violations;
    }

    public static validateRowUrlColumn(dataFlowSink: DataFlowSink, value: DataFlowExpression): IViolation[] | undefined {
        const violations: IViolation[] = [];
        if (dataFlowSink) {
            if (dataFlowSink.fileNameOption === FileNameOption.RowUrlColumn) {
                if (stringIsNullOrEmpty(dataFlowSink.rowUrl.expression)) {
                    violations.push({
                        context: dataFlowSink,
                        message: () => stringResources.DataFlowErrorSpecifyRowUrl_Sink
                    });
                }
                if (dataFlowSink.partitionOption !== 'Default') {
                    violations.push({
                        context: dataFlowSink,
                        message: () => stringResources.DataFlowErrorRowUrlAndPartition_Sink
                    });
                }
            }
        }

        violations.push(...Transformation.validateTemplateExpressions(dataFlowSink, [value], stringResources.DataFlow_ColumnWithRowUrl));
        return violations;
    }

    public static rowFolderUrlColumn(dataFlowSink: DataFlowSink, value: DataFlowExpression): IViolation[] | undefined {
        const violations: IViolation[] = [];
        if (dataFlowSink) {
            if (dataFlowSink.fileNameOption === FileNameOption.RowFolderUrlColumn) {
                if (stringIsNullOrEmpty(dataFlowSink.rowFolderUrlColumn.expression)) {
                    violations.push({
                        context: dataFlowSink,
                        message: () => stringResources.DataFlowErrorSpecifyRowFolderUrl_Sink
                    });
                }
                if (dataFlowSink.partitionOption !== 'Default') {
                    violations.push({
                        context: dataFlowSink,
                        message: () => stringResources.DataFlowErrorRowFolderUrlAndPartition_Sink
                    });
                }
            }
        }

        violations.push(...Transformation.validateTemplateExpressions(dataFlowSink, [value], stringResources.DataFlow_ColumnWithRowUrl));
        return violations;
    }

    public static validateDataFlowSink(dataFlowSink: DataFlowSink): IViolation[] {
        const violations: IViolation[] = [];
        violations.push(...DataFlowSink.validateAlterRow(dataFlowSink));

        return violations;
    }

    public static validateAlterRow(dataFlowSink: DataFlowSink): IViolation[] {
        if (dataFlowSink.requiresAlterRowTransformation && !dataFlowSink.hasAlterRowUpstream) {
            return [{
                context: dataFlowSink,
                message: () => stringResources.DataFlowSinkRequiresAlterRow
            }];
        }
        return [];
    }

    public static validateTTL(owner: DataFlowSink, value: DataFlowExpression): IViolation[] {
        if (owner.sinkDataset instanceof CassandraDataset && !value.templateExpression && !stringIsNullOrEmpty(value.expression)) {
            const val = parseInt(value.expression, 10);
            if (isNaN(val) || (val < 0)) {
                return [{
                    message: () => stringResources.TimeToLiveErrorMessage
                }];
            }
        }
        return Transformation.validateTemplateExpressions(owner, [value], stringResources.ModelName_TimeToLive);
    }

    private static _validateFaultToleranceLinkedService(dataFlowSink: DataFlowSink, faultToleranceLinkedService: LinkedService): IViolation[] {
        const violations = [];
        if (faultToleranceLinkedService instanceof AzureBlobStorageLinkedService && faultToleranceLinkedService.authenticationMethod === azureStorageAuthTypes.Anonymous) {
            violations.push({
                message: () => format(stringResources.Anonymous_BlobStorage_ErrorTemplate, stringResources.DataflowLowerCase)
            });
        }
        return violations;
    }

    /**
     * Validates datasetSettings for sink-specific requirements.
     * Includes:
     * 1. MDF format validation (via mdfFormatValidator) - for Lakehouse Files format selection
     * 2. Update method validation - at least one of insert/delete/upsert/update must be enabled
     * 3. Key columns validation - required for certain sink types
     * 4. CDC validation - required methods when CDC is enabled
     */
    private static async validateDatasetSettings(dataFlowSink: DataFlowSink, value: DataFlowDatasetSettings, violation: IViolation, context: IValidationContext): Promise<IViolation[]> {
        const violations: IViolation[] = [];

        // MDF format validation - same validator as source for consistent behavior
        const mdfViolations = mdfFormatValidator(dataFlowSink, value);
        if (mdfViolations && mdfViolations.length > 0) {
            violations.push(...mdfViolations);
        }

        if (!dataFlowSink.isInlineDataset || dataFlowSink.datasetSettings == null) {
            return violations;
        }

        if (
            (dataFlowSink.datasetSettings instanceof TabularTypeSettings &&
                !(dataFlowSink.datasetSettings instanceof CosmosSqlSettings) &&
                !(dataFlowSink.datasetSettings instanceof DynamicsSettings) &&
                !(dataFlowSink.datasetSettings instanceof AdxSettings)) ||
            dataFlowSink.datasetSettings instanceof DeltaSettings ||
            dataFlowSink.datasetSettings instanceof SqlBaseSettings
        ) {
            if (
                dataFlowSink.datasetSettings.allowInsert.expression === 'false' &&
                dataFlowSink.datasetSettings.allowDelete.expression === 'false' &&
                dataFlowSink.datasetSettings.allowUpsert.expression === 'false' &&
                dataFlowSink.datasetSettings.allowUpdate.expression === 'false'
            ) {
                violations.push({
                    context: dataFlowSink,
                    message: () => stringResources.DataFlowSinkNoUpdateMethodSelected
                });
            }
        }

        // validate key columns
        if (
            dataFlowSink.requiresKeyColumns &&
            !(dataFlowSink.datasetSettings instanceof DynamicsSettings) &&
            dataFlowSink.datasetSettings.keyColumns?.every((keyColumn) => stringIsNullOrEmpty(keyColumn.expression))
        ) {
            violations.push({
                context: dataFlowSink,
                redirectTo: {
                    entity: dataFlowSink,
                    attribute: Metamodel.modelOf(dataFlowSink.datasetSettings).attribute('keyColumns')
                },
                message: () => stringResources.DataFlowSinkRequiresKeyColumn
            });
        }

        const dataflowFormatRegistryService = await getServiceInstance<DataflowFormatRegistryService>(context.contextData, 'DataflowFormatRegistryService');
        if (
            isDataflowSinkEnableAlterRow(dataFlowSink, { dataflowFormatRegistryService }) &&
            (dataFlowSink.datasetSettings.allowDelete.expression === 'false' ||
                dataFlowSink.datasetSettings.allowUpsert.expression === 'false')
        ) {
            const source = findDataFlowSource(dataFlowSink);
            if (isDataflowSourceEnableCdc(source)) {
                if (dataFlowSink.datasetSettings.allowDelete.expression === 'false') {
                    violations.push({
                        message: () =>
                            format(
                                stringResources.RequiredTemplateWhen,
                                stringResources.DataFlowSinkAllowDelete,
                                stringResources.DataFlowEnableCdcLabel,
                                stringResources.EnabledInLowercase
                            )
                    });
                }
                if (dataFlowSink.datasetSettings.allowUpsert.expression === 'false') {
                    violations.push({
                        message: () =>
                            format(
                                stringResources.RequiredTemplateWhen,
                                stringResources.DataFlowSinkAllowUpsert,
                                stringResources.DataFlowEnableCdcLabel,
                                stringResources.EnabledInLowercase
                            )
                    });
                }
            } else if (
                isCosmosDBAnalyticalType(source) &&
                dataFlowSink.datasetSettings.allowUpsert.expression === 'false'
            ) {
                if (isTruthy(dataFlowSink.datasetSettings.allowInsert)) {
                    violations.push({
                        message: () =>
                            format(
                                stringResources.DataFlowSinkCosmosDBASAllowUpsertValidationError,
                                stringResources.DataFlowSinkAllowInsert
                            )
                    });
                }
                if (isTruthy(dataFlowSink.datasetSettings.allowUpdate)) {
                    violations.push({
                        message: () =>
                            format(
                                stringResources.DataFlowSinkCosmosDBASAllowUpsertValidationError,
                                stringResources.DataFlowSinkAllowUpdate
                            )
                    });
                }
            }
        }
        return violations;
    }

    private static validateKeyColumnMappings(dataFlowSink: DataFlowSink): IViolation[] {
        if (
            dataFlowSink.requiresKeyColumns &&
            !(dataFlowSink.sinkDataset instanceof RestResourceDataset) &&
            !(dataFlowSink.sinkDataset instanceof DynamicsEntityDataset) &&
            !(dataFlowSink.sinkDataset instanceof DynamicsCrmEntityDataset) &&
            !(dataFlowSink.sinkDataset instanceof CommonDataServiceEntityDataset) &&
            !(dataFlowSink.datasetSettings instanceof DynamicsSettings) &&
            !(dataFlowSink.datasetSettings instanceof RestSettings)
        ) {
            let missingMapping = '';
            const keyColumns =
                dataFlowSink.isInlineDataset &&
                (dataFlowSink.datasetSettings instanceof TabularTypeSettings ||
                    dataFlowSink.datasetSettings instanceof SqlBaseSettings ||
                    dataFlowSink.datasetSettings instanceof DeltaSettings)
                    ? dataFlowSink.datasetSettings.keyColumns
                    : dataFlowSink.keyColumns;
            for (const keyColumn of keyColumns) {
                for (const columnArgument of keyColumn.argumentsUsed.dataFlowColumns) {
                    if (!dataFlowSink.columns.find((column) => column.name === columnArgument.column.name)) {
                        missingMapping = columnArgument.column.name;
                        break;
                    }
                }
                if (missingMapping || keyColumn.expressionHasError()) {
                    return [
                        {
                            context: dataFlowSink,
                            redirectTo: {
                                entity: dataFlowSink,
                                attribute: Metamodel.modelOf(
                                    dataFlowSink.isInlineDataset ? dataFlowSink.datasetSettings : dataFlowSink
                                ).attribute('keyColumns')
                            },
                            message: () =>
                                sanitizeAndFormatString(
                                    stringResources.DataFlowSinkKeyColumnsNotMapped,
                                    missingMapping || keyColumn.expression
                                )
                        }
                    ];
                }
            }
        }
        return [];
    }

    private static async validatePartitionFileNames(owner: DataFlowSink, value: DataFlowExpression[], violation: IViolation, context: IValidationContext): Promise<IViolation[]> {
        const dsSvc = await getServiceInstance<DatasetRegistryService>(context.contextData, 'DatasetRegistryService');
        const violations = DataFlowSink.validateFileNames(owner, value, FileNameOption.PartitionFileNames,
            stringResources.DataFlowErrorPartitionFileNames_Sink, stringResources.Dataflow_DatasetFileNameOptionPatternFileNames, { dsSvc });
        if (owner.fileNameOption === FileNameOption.PartitionFileNames && owner.partitionMethod === 'key') {
            violations.push({
                context: owner,
                message: () => stringResources.DataFlowErrorFileNameAndKeyPartition_Sink
            });
        }
        return violations;
    }

    private static async validateFilePattern(owner: DataFlowSink, value: DataFlowExpression, violation: IViolation, context: IValidationContext): Promise<IViolation[]> {
        const dsSvc = await getServiceInstance<DatasetRegistryService>(context.contextData, 'DatasetRegistryService');
        return DataFlowSink.validateFileNames(owner, [value], FileNameOption.FilePattern,
            stringResources.DataFlowErrorFilePattern_Sink, stringResources.Dataflow_DatasetFileNameOptionPatternLabel, { dsSvc });
    }

    private static async validateSingleFile(owner: DataFlowSink, value: DataFlowExpression, violation: IViolation, context: IValidationContext): Promise<IViolation[]> {
        const dsSvc = await getServiceInstance<DatasetRegistryService>(context.contextData, 'DatasetRegistryService');
        return DataFlowSink.validateFileNames(owner, [value], FileNameOption.SingleFile,
            stringResources.DataFlowErrorSingleFile_Sink, stringResources.Dataflow_DatasetFileNameOptionSingleFileLabel, { dsSvc });
    }

    private static validateFileNames(dataFlowSink: DataFlowSink, dataflowExpressions: DataFlowExpression[],
        fileNameOption: FileNameOption, emptyValueError: string, attributeName: string, services: { dsSvc: DatasetRegistryService }): IViolation[] {
        const violations: IViolation[] = Transformation.validateTemplateExpressions(dataFlowSink, dataflowExpressions, attributeName);
        if ((isFileTypeDataset(dataFlowSink.sinkDataset, services) || isFileTypeCategoryDataset(dataFlowSink.sinkDataset, services)) && dataFlowSink.fileNameOption === fileNameOption) {
            dataflowExpressions.forEach((expression) => {
                if (stringIsNullOrWhitespace(expression.expression)) {
                    violations.push({
                        context: dataFlowSink,
                        message: () => emptyValueError
                    });
                }
            });
        }
        return violations;
    }

    private static validatePreSQLScripts(owner: DataFlowSink, value: DataFlowExpression): IViolation[] {
        return Transformation.validateTemplateExpressions(owner, [value], stringResources.Dataflow_DatasetSQLPreScriptLabel);
    }

    private static validatePostSQLScripts(owner: DataFlowSink, value: DataFlowExpression): IViolation[] {
        return Transformation.validateTemplateExpressions(owner, [value], stringResources.Dataflow_DatasetSQLPostScriptLabel);
    }

    private static validateKeyColumns(owner: DataFlowSink, value: DataFlowExpression[]): IViolation[] {
        if (
            owner.requiresKeyColumns &&
            !(owner.datasetSettings instanceof RestSettings) &&
            !(owner.sinkDataset instanceof RestResourceDataset)
        ) {
            const keyColumns =
                owner.isInlineDataset && owner.datasetSettings instanceof TabularTypeSettings
                    ? owner.datasetSettings.keyColumns
                    : value;
            return Transformation.validateTemplateExpressions(
                owner,
                keyColumns,
                stringResources.DataFlowDatasetKeyColumns
            );
        }
        return [];
    }

    private static validateEmptyLinesAsHeader(owner: DataFlowSink, value: DataFlowExpression): IViolation[] {
        return Transformation.validateTemplateExpressions(owner, [value], stringResources.SkipLineCountLabel);
    }

    private static validateBatchSize(owner: DataFlowSink, value: DataFlowExpression): IViolation[] {
        if (owner.sinkDataset instanceof AzureCosmosDbV2Dataset && !value.templateExpression && !stringIsNullOrEmpty(value.expression)) {
            const val = parseInt(value.expression, 10);
            if (isNaN(val)) {
                return [{
                    message: () => stringResources.BatchSizeErrorMessage
                }];
            }
        }
        return Transformation.validateTemplateExpressions(owner, [value], stringResources.ModelName_BatchSize);
    }

    private static validateSchema(owner: DataFlowSink, value: DataFlowExpression): IViolation[] {
        return Transformation.validateTemplateExpressions(owner, [value], stringResources.DataFlowSourcePanelValidateSchema);
    }

    private static validateAllowInsert(owner: DataFlowSink, value: DataFlowExpression): IViolation[] {
        return Transformation.validateTemplateExpressions(owner, [value], stringResources.DataFlowSinkAllowInsert);
    }

    private static async validateAllowDelete(owner: DataFlowSink, value: DataFlowExpression, violation: IViolation, context: IValidationContext): Promise<IViolation[]> {
        const violations: IViolation[] = Transformation.validateTemplateExpressions(owner, [value], stringResources.DataFlowSinkAllowDelete);
        const dataflowFormatRegistryService = await getServiceInstance<DataflowFormatRegistryService>(context.contextData, 'DataflowFormatRegistryService');
        if (
            !owner.isInlineDataset
            && isDataflowSinkEnableAlterRow(owner, { dataflowFormatRegistryService })
            && value.expression === 'false'
        ) {
            const source = findDataFlowSource(owner);
            if (isDataflowSourceEnableCdc(source)) {
                violations.push({
                    message: () => format(stringResources.RequiredTemplateWhen, stringResources.DataFlowSinkAllowDelete, stringResources.DataFlowEnableCdcLabel, stringResources.EnabledInLowercase)
                });
            }
        }
        return violations;
    }

    private static async validateAllowUpsert(owner: DataFlowSink, value: DataFlowExpression, violation: IViolation, context: IValidationContext): Promise<IViolation[]> {
        const violations: IViolation[] = Transformation.validateTemplateExpressions(owner, [value], stringResources.DataFlowSinkAllowUpsert);
        const dataflowFormatRegistryService = await getServiceInstance<DataflowFormatRegistryService>(context.contextData, 'DataflowFormatRegistryService');
        if (
            !owner.isInlineDataset
            && isDataflowSinkEnableAlterRow(owner, { dataflowFormatRegistryService })
            && value.expression === 'false'
        ) {
            const source = findDataFlowSource(owner);
            if (isDataflowSourceEnableCdc(source)) {
                violations.push({
                    message: () => format(stringResources.RequiredTemplateWhen, stringResources.DataFlowSinkAllowUpsert, stringResources.DataFlowEnableCdcLabel, stringResources.EnabledInLowercase)
                });
            } else if (isCosmosDBAnalyticalType(source)) {
                if (isTruthy(owner.allowInsert)) {
                    violations.push({
                        message: () => format(stringResources.DataFlowSinkCosmosDBASAllowUpsertValidationError, stringResources.DataFlowSinkAllowInsert)
                    });
                }
                if (isTruthy(owner.allowUpdate)) {
                    violations.push({
                        message: () => format(stringResources.DataFlowSinkCosmosDBASAllowUpsertValidationError, stringResources.DataFlowSinkAllowUpdate)
                    });
                }
            }
        }
        return violations;
    }

    private static validateAllowUpdate(owner: DataFlowSink, value: DataFlowExpression): IViolation[] {
        return Transformation.validateTemplateExpressions(owner, [value], stringResources.DataFlowSinkAllowUpdate);
    }

    private static validateskipKeyWrites(owner: DataFlowSink, value: DataFlowExpression): IViolation[] {
        return Transformation.validateTemplateExpressions(owner, [value], stringResources.DataFlowSinkSkipKeyWrites);
    }

    private static validateClearFolder(owner: DataFlowSink, value: DataFlowExpression): IViolation[] {
        return Transformation.validateTemplateExpressions(owner, [value], stringResources.DataFlowClearFolder);
    }

    private static validateQuoteAll(owner: DataFlowSink, value: DataFlowExpression): IViolation[] {
        return Transformation.validateTemplateExpressions(owner, [value], stringResources.DataFlowQuoteAll);
    }

    private static validateHeaders(owner: DataFlowSink, value: DataFlowExpression): IViolation[] {
        return Transformation.validateTemplateExpressions(owner, [value], stringResources.HeadersLabel);
    }

    private static validateTimeout(owner: DataFlowSink, value: DataFlowExpression): IViolation[] {
        return Transformation.validateTemplateExpressions(owner, [value], stringResources.Timeout);
    }

    private static validateCacheSinkOutput(owner: DataFlowSink, value: DataFlowExpression): IViolation[] {
        return Transformation.validateTemplateExpressions(owner, [value], stringResources.OutputLabel);
    }

    private static validateSaveOrder(owner: DataFlowSink, value: DataFlowExpression): IViolation[] {
        if (value.templateExpression && !value.returnsCorrectType) {
            return [{
                message: () => format(stringResources.DataFlowExpressionUnexpectedReturnType, `'${stringResources.DataFlowSettingsUseCustomSinkOrderingLabel}'`, value.requiredReturnType),
                redirectTo: {
                    entity: owner.owner
                }
            }];
        }
        return [];
    }

    public get parameters(): OwnedSet<Parameter> {
        return this.sinkDataset && this.sinkDataset.parameters;
    }

    public get partitionKeyRequired(): boolean {
        return this.sinkDataset && this.sinkDataset instanceof AzureCosmosDbV2Dataset
            && (isTruthy(this.allowUpdate)
                || isTruthy(this.allowDelete)
                || this.tableActionOption === TableActionOption.Recreate);
    }

    public get includeSchemaLinkedService(): boolean {
        return this.datasetSettings.includeSchemaLinkedServiceInResource;
    }

    // eslint-disable-next-line no-empty,@typescript-eslint/no-empty-function
    public initializeTransformation(contextData: IResourceContext): void { }

    // eslint-disable-next-line no-empty, @typescript-eslint/no-empty-function
    public preRemoveAction(): void { }

    // For Sink there is no output stream, so don't add it.
    public addOutputStream(_streamName?: string): DataFlowStream {
        return undefined;
    }

    public updateInputTransformationCacheInternal(): void {
        super.updateInputTransformationCacheInternal();
        this.hasAlterRowTransformationInNestedFlowlets(this.getAllUpTransformations());
        this.hasAssertUpstream = !!this.getAllUpTransformations().find((transformation) => transformation instanceof AssertTransformation);
    }

    // TODO: Handle flowlet circular dependencies separately
    public hasAlterRowTransformationInNestedFlowlets(transformationsInFlowlet: Transformation[]): void {
        this.hasAlterRowUpstream = !!Array.from(transformationsInFlowlet).find((transformation: Transformation) => transformation instanceof AlterRowTransformation);
        if (this.hasAlterRowUpstream) {
            return;
        }
        // If alter row transformation is not found, check for nested flowlets and traverse through each of them.
        const executeFlowletTransformations = Array.from(transformationsInFlowlet).filter((transformation: Transformation) => transformation.hasOwnProperty('flowlet'));
        for (const executeFlowletTransformation of executeFlowletTransformations) {
            if(!this.hasAlterRowUpstream) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                this.hasAlterRowTransformationInNestedFlowlets((executeFlowletTransformation as any).flowlet.transformations);
            }
        }
    }

    public get owner(): DataFlow {
        return (super.getOwner() as DataFlow);
    }

    protected get inputColumns(): DataFlowColumn[] {
        return this.inputStreams.length > 0 ? this.inputStreams[0].producer.outputColumns() : [];
    }

    public outputAliasPatternValidationRequired(): boolean {
        if (!this.sinkDataset) {
            return false;
        }
        return this.sinkDataset instanceof ParquetDataset || (this.sinkDataset instanceof AzureSqlDWTableDataset && this.staged);
    }

    public get isDelimitedWithoutHeader(): boolean {
        return this.sinkDataset instanceof DelimitedTextDataset &&
            !this.sinkDataset.firstRowAsHeader &&
            (this.sinkDataset.columns.length === 0 || stringIsNullOrEmpty(this.sinkDataset.columns[0].name));
    }

    public get isUpdateMethodSelected(): boolean {
        if (!this.requiresUpdateMethod) {
            return true;
        }

        return this.isInlineDataset
            ? (
                this.datasetSettings.allowInsert.expression === 'true'
                || this.datasetSettings.allowDelete.expression === 'true'
                || this.datasetSettings.allowUpdate.expression === 'true'
                || this.datasetSettings.allowUpsert.expression === 'true'
            )
            : (
                this.allowInsert.expression === 'true'
                || this.allowDelete.expression === 'true'
                || this.allowUpdate.expression === 'true'
                || this.allowUpsert.expression === 'true');
    }

    public get requiresUpdateMethod(): boolean {
        return this.isInlineDataset
            ? (
                (
                    this.datasetSettings instanceof TabularTypeSettings
                    && !(this.datasetSettings instanceof AdxSettings)
                    && !(this.datasetSettings instanceof CosmosSqlSettings)
                    && !(this.datasetSettings instanceof DynamicsSettings)
                )
                || this.datasetSettings instanceof DeltaSettings
                || this.datasetSettings instanceof SqlBaseSettings
            )
            : (
                (
                    this.sinkDataset instanceof TabularTypeDataset
                    && !(this.sinkDataset instanceof AzureCosmosDbV2Dataset)
                    && !(this.sinkDataset instanceof DynamicsEntityDataset)
                    && !(this.sinkDataset instanceof DynamicsCrmEntityDataset)
                    && !(this.sinkDataset instanceof CommonDataServiceEntityDataset)
                    && !(this.sinkDataset instanceof AzureDataExplorerDataset)
                )
            );
    }

    public get requiresAssertTransformation(): boolean {
        return isTruthy(this.outputAssertFailedRowsToAltStorage);
    }

    public get requiresKeyColumns(): boolean {
        return this.isInlineDataset
            ? (
                (
                    this.datasetSettings instanceof TabularTypeSettings
                    && !(this.datasetSettings instanceof AdxSettings)
                    && !(this.datasetSettings instanceof CosmosSqlSettings)
                )
                || this.datasetSettings instanceof DeltaSettings
                || this.datasetSettings instanceof SqlBaseSettings
                || this.datasetSettings instanceof RestSettings
            )
            && (isTruthy(this.datasetSettings.allowDelete) || isTruthy(this.datasetSettings.allowUpsert) || isTruthy(this.datasetSettings.allowUpdate))
            : (
                (
                    (
                        this.sinkDataset instanceof TabularTypeDataset
                        && !(this.sinkDataset instanceof AzureDataExplorerDataset)
                        && !(this.sinkDataset instanceof AzureCosmosDbV2Dataset)
                    )
                    || this.sinkDataset instanceof RestResourceDataset
                    || this.sinkDataset instanceof LakehouseTableDataset
                )
                && (isTruthy(this.allowDelete) || isTruthy(this.allowUpsert) || isTruthy(this.allowUpdate))
            )
            || (this.sinkDataset instanceof CassandraDataset && this.tableActionOption === TableActionOption.Recreate);
    }

    public get requiresAlterRowTransformation(): boolean {
        const source = findDataFlowSource(this);
        if (isDataflowSourceEnableCdc(source) || isCosmosDBAnalyticalType(source)) {
            // enable CDC and Cosmos DB analytical type do not need to alter row
            return false;
        }
        const sink = findDataFlowSink(this);
        if (isCassandraType(sink)) {
            // Cassandra type does not need alter row
            return false;
        }
        return this.requiresKeyColumns;
    }

    public get alterRowEnabled(): boolean {
        // now only used by TabularTypeAdapter
        return this.isInlineDataset
            ? (
                (
                    this.datasetSettings instanceof TabularTypeSettings
                    && !(this.datasetSettings instanceof AdxSettings)
                )
                || this.datasetSettings instanceof DeltaSettings
                || this.datasetSettings instanceof SqlBaseSettings
                || this.datasetSettings instanceof RestSettings
            )
            : (
                (
                    this.sinkDataset instanceof TabularTypeDataset
                    && !(this.sinkDataset instanceof AzureDataExplorerDataset)
                )
                || this.sinkDataset instanceof RestResourceDataset
            );
    }

    public updateTransformationCache(): void {
        super.updateTransformationCache();
        // Since sink output columns are updated at last, check for sink output renamed columns references.
        this.updateParametersOnColumnRename(this.distinctOutputColumnRefs);
        const dataFlowArguments = getDataFlowBaseArguments(this.dataflow);
        this.dataflow.parameters.forEach((param) => {
            if (GlobalVariables.validateDataFlowAPI) {
                asyncUpdateIdentifiers(dataFlowArguments, param.defaultValueExpression, this);
            } else {
                updateIdentifiers(dataFlowArguments, param.defaultValueExpression);
            }
        });
        if (this.isInlineDataset && this.datasetSettings != null) {
            // Update datasetSettings cache
            updateDatasetSettingsCache(this.datasetSettings, dataFlowArguments);
        }
    }

    public insertInputStream(streamModel: DataFlowStream, index?: number): void {
        super.insertInputStream(streamModel, index);
        initializeDataflowSink(this);
    }

    protected getOutputDependentExpressions(): DataFlowExpression[] {
        return GlobalVariables.enableDataflowInlineSinkKeyColumnValidation
            ? this.isInlineDataset
                ? this.partitionConditions.concat(this.datasetSettings?.keyColumns ?? [])
                : this.partitionConditions.concat(this.keyColumns)
            : this.partitionConditions.concat(this.keyColumns);
    }

    protected getGeneratedDescription(): string {
        return this.isCacheSink ? stringResources.SinkCacheDescription
            : this.sinkDataset ? format(stringResources.SinkDescription, this.sinkDataset.name)
                : stringResources.SinkNoDatasetDescription;
    }

    protected validateOutputColumnNames(context: IValidationContext): IViolation[] | undefined {
        const violations: IViolation[] = super.validateOutputColumnNames(context);

        if (this.outputAliasPatternValidationRequired()) {
            this.distinctOutputColumnRefs.forEach((column) => {
                if (!Constants.dataFlowColumnNameValidation.test(column.name)) {
                    violations.push({
                        context: this,
                        message: () => sanitizeAndFormatString(stringResources.DataFlowSinkSpecialCharactersError, this.name)
                    });
                }
            });
        }

        return violations;
    }
}
